import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@app/environments/environment";
import { Priority } from "@app/shared/components/que-mnger-expand-config/priority";
import { Enums } from "@app/util/enums";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  constructor() { }

  /* Donot modify the url here. Please modify the backend url in environment.ts file */
  public KPID_URL = environment.backendUrl; // Not be changed here

  /**
   * Topic feature endpoints. These are now served by the KPI server (KPID_URL),
   * which proxies the requests to the topic backend (mounted under /github-topic),
   * instead of the UI calling the topic server directly.
   */
  public TOPIC_APIS = {
    PR_HISTORY: '/github-topic/pull_request/history',
    ALL_REPOS: '/github-topic/repository/all',
    ALL_TOPICS: '/github-topic/topic/all',
  };

  /**
   * header for isLoader false. Can use it in any service.
   */
  headers = new HttpHeaders({ isLoader: "false" });

  /**
   * This  contains API endpoints for various features of a workflow. The object has the following properties:
   * FEATURE: The endpoint for retrieving feature data.
   *FEATURE_DATA: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving data for a workflow.
   *FEATURE_QUEUED: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving queued workflow data.
   *FEATURE_QUEUE_CARDS: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving queue cards for a workflow.
   *FEATURE_PIPELINE_EXEC: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving executed workflow data.
   *FEATURE_FAILED_PIPELINE: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving failed workflow data.
   *FEATURE_PIPELINE_LEAD_TIME: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving workflow lead time data.
   *FEATURE_TIME_TO_RESTORE_PIPELINE: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving data on the time taken to restore a workflow.
   *FEATURE_QUEUED_PIPELINE_DATA_FOR_CONFIG: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving queued workflow data.
   *Overall, this object is used to define the API endpoints for various features of a workflow, making it easier to access and retrieve data for those features.
   */
  public FEATURE_APIS = {
    FEATURE: "/feature",
    FEATURE_DATA: (duration: String, id: number) =>
      `/data?wfId=${id}&duration=${duration}`,
    FEATURE_QUEUED: (duration: String, id: number) =>
      `/queued?wfId=${id}&duration=${duration}`,
    FEATURE_QUEUE_CARDS: (duration: String, id: number) =>
      `/queue-cards?wfId=${id}&duration=${duration}`,
    FEATURE_PIPELINE_EXEC: (duration: String, id: number) =>
      `/executed-workflow?wfId=${id}&duration=${duration}`,
    FEATURE_FAILED_PIPELINE: (duration: String, id: number) =>
      `/failed-workflow?wfId=${id}&duration=${duration}`,
    FEATURE_PIPELINE_LEAD_TIME: (duration: String, id: number) =>
      `/workflow-lead-time?wfId=${id}&duration=${duration}`,
    FEATURE_TIME_TO_RESTORE_PIPELINE: (duration: String, id: number) =>
      `/time-to-restore-workflow?wfId=${id}&duration=${duration}`,
    FEATURE_QUEUED_PIPELINE_DATA_FOR_CONFIG: (duration: String, id: number) =>
      `/queued-workflow-data-for-config?wfId=${id}&duration=${duration}`,
  };
  /**
   * This is a object that contains API endpoints for managing workflows. The object has the following properties:

   *WORKFLOW: The endpoint for retrieving workflow data.
   *WORKFLOW_DETAILS: The endpoint for retrieving details of all workflows.
   *WORKFLOW_PERFORMANCE: The endpoint for retrieving performance data for workflows.
   *WORKFLOW_IS_AV: A function that takes in a repoUrl string, a branch string, and a brCond string and returns the endpoint for checking if a workflow is active.
   *WORKFLOW_ADD: The endpoint for adding a new workflow.
   *WORKFLOW_EXECUTED: A function that takes in a duration string and a wfId number and returns the endpoint for retrieving executed workflow data.
   *WORKFLOW_EXE_RES: A function that takes in a jobId and returns the endpoint for retrieving execution results for a workflow.
   *WORKFLOW_QG_RES: A function that takes in a jobId and returns the endpoint for retrieving quality gate results for a workflow.
   *WORKFLOW_NAME: A function that returns the endpoint for retrieving names of all workflows.
   *WORKFLOW_UPDATE: A function that takes in a id number and returns the endpoint for updating a workflow.
   *WORKFLOW_DELETE: A function that takes in a id and returns the endpoint for soft-deleting a workflow.
   *Overall, this object is used to define the API endpoints for managing workflows.
   */
  public WORKFLOW_APIS = {
    WORKFLOW: "/workflow",
    WORKFLOW_DETAILS: (id: number) => `/details/${id}`,
    WORKFLOW_IS_AV: `/is-active`,
    WFQ_PERF: (days: number, type: string) => `/performance?days=${days}&type=${type}`,
    WORKFLOW_ADD: (activeWf: number | null) => `/add?disable=${activeWf}`,
    WORKFLOW_EXECUTED: (duration: String, id: number) =>
      `/details/executed?wfId=${id}&duration=${duration}`,
    WORKFLOW_EXE_RES: (jobId: any) => `/details/exe-res?jobId=${jobId}`,
    WORKFLOW_QG_RES: (jobId: any) => `/details/qg-res?jobId=${jobId}`,
    WORKFLOW_NAME: (isconfigBuild: boolean = false) => `/names?forBuilderPage=${isconfigBuild}`,
    WORKFLOW_UPDATE: (id: number, disable: number = -1) =>
      `/update/${id}?disable=${disable}`,
    WORKFLOW_DELETE: (id: any) => `/soft-delete/${id}`,
    WORKFLOW_SOFT_DELETE: (id: any) => `/delete/${id}`,
  };

  /**
   * Represents a set of routes related to the workflow queue.
   */
  public WF_QUEUE = {
    // Route for accessing the workflow queue
    WFQ: "/workflow-queue",
    /**
     * Route for updating the priority of a job in the workflow queue.
     * @param {any} jobId - The unique identifier of the job.
     * @param {Priority} priority - The new priority value for the job.
     * @returns {string} - The route URL for updating the job priority.
     */
    WFQ_UPDATE_PRIO: (jobId: any, priority: Priority) =>
      `/update-priority/${jobId}?priority=${priority}`,
    WFQ_QUEUE_INP_ARGS: (jobId: any) => `/input-arguments/${jobId}`,
    WFQ_ABORT: (jobId: any, status: any) => `/abort/${status}/${jobId}`,
    EXISTING_WF_TRIGGER: (jobId: any) => `/rerun?jobId=${jobId}`,
    WFQ_CANCEL_DAG: (id: number) => `/abort-dag/${id}`,
    WFQ_TRIGGER_DYNAMIC: (jobId: string) => `/trigger/${jobId}?triggerType=dynamic`,
  };

  /**
   * Represents a set of API routes related to the dashboard functionality.
   */
  public DASHBOARD_APIS = {
    ERROR_DIADNOSTICS: {
      ML_GROUP: "/ml-group",
      /**
       * Route for retrieving group error details for machine learning.
       * @param {string} grpName - The name of the machine learning group.
       * @param {number} weekNum - The week number for which to retrieve error details.
       * @returns {string} - The route URL for retrieving machine learning group error details.
       */
      GET_GROUP_ERROR_DETAILS: "/get-ml-error-count-details-weekly",
      /**
       * Route for retrieving cluster details for machine learning.
       * @param {string} grpName - The name of the machine learning group.
       * @param {number} weekNum - The week number for which to retrieve cluster details.
       * @returns {string} - The route URL for retrieving machine learning cluster details.
       */
      GET_CLUSTER_DETAILS: (grpName: string, weekNum: number) =>
        `/get-ml-key-count?groupName=${grpName}&weekNum=${weekNum}`,
      GET_ERROR_DETAILS: (keyword: number, weekNum: number) =>
        `/author-count?keyword=${keyword}&weekNum=${weekNum}`,
    },
    SUMMARY: {
      DASHBOARD: "/dashboard",
      WF_SUMMARY: (days: number, wfId: number) =>
        `/workflow-summary?days=${days}&wfId=${wfId}`,
    },
    LICENSE_DATA: "/license/status",
    MOST_USED_WORKFLOW_DATA: "/dashboard/most-used-workflows",
    DISTRIBUTION: {
      WF_EXECUTED: (days: number) =>
        `/dashboard/distribution/workflow-count?days=${days}`,
      WF_SUCCESS: (days: number) =>
        `/dashboard/distribution/workflow-success-count?days=${days}`,
      WF_FAILED: (days: number) =>
        `/dashboard/distribution/workflow-fail-count?days=${days}`,
      WF_QUEUED: (days: number) =>
        `/dashboard/distribution/workflow-queued-count?days=${days}`,
      WF_IN_PROGRESS: (days: number) =>
        `/dashboard/distribution/workflow-inprogress-count?days=${days}`,
      WF_ABORTED: (days: number) =>
        `/dashboard/distribution/workflow-aborted-count?days=${days}`,
    },
  };

  /**
   * API endpoints for the Dynamic Triggered Configurations page.
   * Lists all dynamic configuration runs that have been triggered,
   * filterable by duration.
   */
  public DYNAMIC_CONFIGS = {
    DC_BASE: '/dag',
    DC_GET_ALL: (offset: number, size: number, schedJobId: string | null = null) =>
      `/dynamic?offset=${offset}&size=${size}` + (schedJobId ? `&schedJobId=${encodeURIComponent(schedJobId)}` : ''),
    DC_GET_EXECUTION_DETAILS: (executionId: string, dagId: number) =>
      `/execution-details/${executionId}/${dagId}`,
  };

  /**
   *An object containing API endpoints for configuring workflows.
   *Endpoints include retrieving workflows, input arguments, quality gates,
   *branch lists, as well as updating and deleting workflows.
   */
  public CONFIG_BUILDER_APIS = {
    WF_HUB: "/wf-hub",
    GET_WORKFLOWS: "/names",
    GET_WF_DETAILS: "/all",
    GET_WF_ON_COND: (repoName: string, branch: string, workflow: string) => `/wfh-dtl-by-wf-dtl?repoName=${repoName}&branch=${branch}&workflow=${workflow}`,
    GET_WF_ACTION_URL: (actionUrl: string) => `/wfh-dtl-actionUrl?actionUrl=${actionUrl}`,
    WF_CLOUD_ORCH: "/cloud-orch",
    WF_EVENT_ACTION: "/workflow-event-action",
    GET_EVENTS: "/workflow-event/all",
    WF_EVENT: "/workflow-event",
    GET_INPUT_ARGS: (wfId: number) => `/input-arguments?wfId=${wfId}`,
    GET_INPUT_ARGS_FROM_WFHUB_WFJOB: (jobId: any) => `/input-args-and-qual-gate?jobId=${jobId}`,
    GET_QUALITY_GATES: (wfhId: number) =>
      `/quality-gates-by-workflow-hub-id?wfhId=${wfhId}`,
    GET_WF_QUALITY_GATES: (wfId: number) =>
      `/quality-gates-by-workflow-id?wfId=${wfId}`,
    GET_BRANCH_LIST: (repo: string, owner: string) =>
      `/branches?repo=${repo}&owner=${owner}`,
    WORKFLOW_UPDATE: (id: number) => `/update/${id}`,
    WORKFLOW_DELETE: (id: any) => `/delete/${id}`,
    WF_RUNNER_CLOUD_ORCH_LABELS: (os: string, type: string) =>
      `/labels?os=${os}&platform=${type}`,
    MANUAL_TRIGGER: (id: number, branch: string) =>
      `/trigger/${id}?branch=${branch}`,
    WFH_UPDATE: (id: number) => `/update/${id}`,
    GET_ACTIONS: (id: number) => `/by-event-id?eventId=${id}`,
    GET_PAYLOAD: (eventId: number) => `/payload?eventId=${eventId}`,
  };

  /**
   *An object containing API endpoints for configuring workflows.
   *Endpoints include getting issue templates and component on specific issue template
   */
  public ISSUE_TMPL_APIS = {
    ISSUE_TMPL: "/issue-tmpl",
    GET_ISSUE_TMPL_LIST: (rpUrl: string, brNm: string) => `/list?repoUrl=${rpUrl}&branchName=${brNm}`,
    ISSUE_TMPL_COMP: (id: number) => `/comp?id=${id}`,
  };

  public LABEL_APIS = {
    LABEL: "/label",
    VIEW_ALL: "/view-all",
    ADD: "/add",
    UPDATE: (id: number) => `/update/${id}`,
    DELETE: (id: number) => `/delete/${id}`,
  };

  /**
   * AUTH_APIS object:
   * AUTH : A string constant represnting the endpoint for authentication related api's.
   * AUTH_GROUPS : API endpoint for fetching the assigned groups to a user.
   * AUTH_ADD_USER : API endpoint for adding a user.
   */
  public AUTH_APIS = {
    AUTH: "/auth",
    AUTH_GROUPS: (gmId: string, email: string) =>
      `/groups?gmId=${gmId}&email=${email}`,
  };

  /**
   * This is an object that contains various API endpoints related to feature management.
   * Here are the descriptions for each of the API endpoints:
   * FEATURE: API endpoint for fetching all features
   * FEAT_ACCESS: API endpoint for fetching all features accessible by the authenticated user
   * FEAT_ADD: API endpoint for adding a new feature
   * FEAT_ADD_GROUPS: API endpoint for adding groups to a feature
   * FEAT_REMOVE_GROUPS_FOR_ALL: API endpoint for removing a group from all features
   * FEAT_REMOVE_GROUP: API endpoint for removing a group from a specific feature
   */
  public FEAT_APIS = {
    FEATURE: "/feature",
    // FEAT_ACCESS : (group:string) => `/accessible?group=${group}`,
    FEAT_ACCESS: "/accessible",
    FEAT_ADD: "/add",
    FEAT_ADD_GROUPS: (feature: string) => `/add-groups?feature=${feature}`,
    FEAT_REMOVE_GROUPS_FOR_ALL: (group: string) =>
      `/remove-group-from-all?group=${group}`,
    FEAT_REMOVE_GROUP: (feature: string) => `/remove-group?feature=${feature}`,
  };

  /**
   * This is an object containing API endpoints related to enumerations.
   * GROUP : A string constant representing the endpoint for group-related enumerations.
   * ENUM_ALL : A string constant representing the endpoint to retrieve all enumerations.
   */
  public ENUM_APIS = {
    ENUM: "/enum",
    ENUM_ALL: (enumName: Enums) => `/${enumName}/all`,
  };

  /**
   * APPLICATION_FEAT object:
   * APP_FEAT: Represents the endpoint for retrieving information related to application features.
   * APP_FEAT_ALL: Represents the endpoint for retrieving all application features.
   * APP_FEAT_ADD_GRP: Represents the endpoint for adding groups to an application feature, with page representing the page and feature representing the feature.
   */
  public APPLICATION_FEAT = {
    APP_FEAT: "/application-features",
    APP_FEAT_ALL: "/all",
  };

  /**
   * FEAT_ACCS object:
   * APP_FA: Represents the endpoint for retrieving information related to features access.
   * APP_FA_UPDATE: Represents the endpoint for updated the necessary feature access.
   */
  public FEAT_ACCS = {
    APP_FA: "/feature-access",
    APP_FA_UPDATE: "/update",
  };

  /**
   * TOOL_APIS object:
   * TOOL: Represents the endpoint for retrieving information related to tools.
   * TOOL_ALL: Represents the endpoint for retrieving all tools infromation.
   * TOOL_ADD: Represents the endpoint for adding a new tool.
   */
  public TOOL_APIS = {
    TOOL: "/tool",
    TOOL_ALL: "/all",
    TOOL_ADD: "/add",
    TOOL_UPD: (id: number) => `/update/${id}`,
    TOOL_DLT: (id: number) => `/delete/${id}`,
  };

  /**
   * FLT_LIC_SRV object:
   * FLT_LIC: Represents the endpoint for retrieving information related to floating license server.
   * FLT_LIC_ALL: Represents the endpoint for retrieving all information on floating license server.
   * FLT_LIC_ADD: Represents the endpoint for adding a new floating license server.
   */
  public FLT_LIC_SRV = {
    FLT_LIC: "/flt-lic-srvr",
    FLT_LIC_ALL: "/flt-lic-srvr-all",
    FLT_LIC_ADD: "/flt-lic-srvr-add",
  };

  /**
   * WF_RUN_APIS object:
   * WF_RUN: Represents the endpoint for retrieving information related to workflow jobs.
   * WF_RUN_BY_ID: Represents the endpoint for retrieving information related to workflow jobs executed.
   * WF_RUN_BY_JOB_ID: Represents the endpoint for retrieving information related to job executed stages.
   */
  public WF_RUN_APIS = {
    WF_RUN: "/wf-job",
    // Conditional parameters to the api call
    WF_RUN_BY_ID: (days: number, wkflId: number, offset?: number, size?: number) =>
      `/by-wkfl-id?days=${days}&wkflId=${wkflId}${offset !== undefined ? `&offset=${offset}` : ''}${size !== undefined ? `&size=${size}` : ''}`,
    WF_RUN_BY_JOB_ID: (gnChart: boolean, jobId: string) =>
      `/by-job-id?gnChart=${gnChart}&jobId=${jobId}`,
    
  };

  /**
   * INTEGRATED_WF object:
   * IW: Represents the endpoint for retrieving information related to integrated workflows.
   * IW_ALL: Represents the endpoint for retrieving all integrated workflow.
   * IW_BY_ID: Represents the endpoint for retrieving any integrated workflow by id.
   * IW_SCHEDULE: Represents the endpoint for adding a new scheduled integrated workflow.
   */
  public INTEGRATED_WF = {
    IW: "/integrated-workflow",
    IW_ALL: "/all",
    IW_BY_ID: (iwId: number) => `/by-id?id=${iwId}`,
    IW_SCHEDULE: "/scheduled-workflow",
    IW_UPD_DEL: (iwId: number) => `/update/${iwId}`,
  };

  /**
   * SCHEDULER_APIS object:
   * SCHEDULER: Base endpoint for scheduler operations.
   * ADD: Endpoint for creating a scheduler entry.
   */
  public SCHEDULER_APIS = {
    SCHEDULER: '/workflow-scheduler',
    ADD: '/add',
    ALL: '/all',
    BY_ID: (id: number) => `/${id}`,
    EXECUTIONS: (id: number) => `/${id}/executions`,
    UPDATE: (id: number) => `/update/${id}`,
  };

  /**
   * JOB_EXECUTIONS_APIS object:
   * BASE: Base endpoint for job executions.
   * EXECUTIONS: Endpoint for executions of all schedulers in a UTC time range (from / to query params).
   */
  public JOB_EXECUTIONS_APIS = {
    BASE: '/workflow-scheduler',
    EXECUTIONS: '/executions',
  };

  /**
   * User object
   * USER : Represents the endpoint for retriving information related to user
   * USER_DETAILS : Endpoint for retriving the details of user passed
   */
  public USER = {
    USER: "/user",
    AUTH_ADD_USER: "/add",
    USER_DETAILS: (Id: string) => `/details?username=${Id}`,
  }

  /**
   * GITHUB_APIS object:
   * This object contains API endpoints for GitHub integration functionality used in sync-with-github operations.
   * 
   * GITHUB: Base endpoint for GitHub repository-related operations ("/github/repo")
   * WORKFLOW: Base endpoint for workflow operations ("/workflow") 
   * WORKFLOW_HUB: Base endpoint for workflow hub operations ("/wf-hub")
   * ORG_DETAILS: Endpoint for retrieving all organizations from GitHub that the user has access to ("/wf-hub/get-orgs")
   * REPO_DETAILS: Function that takes organization name, page number, and per_page count to return endpoint for retrieving paginated repositories for that organization
   * BRANCH_DETAILS: Function that takes organization name and repository name to return endpoint for retrieving all branches available in the specified repository
   * GET_ISSUE_TMPL_LIST: Function that takes branch, organization, path, and repository name to return endpoint for retrieving issue templates from GitHub repository
   * UPDATE_ISSUE_TMPL_LIST: Endpoint for synchronizing/updating issue templates with GitHub ("/sync-files")
   */
  public GITHUB_APIS = {
    GITHUB: "/github/repo",
    WORKFLOW: "/workflow",
    WORKFLOW_HUB: "/wf-hub",
    ORG_DETAILS: "/get-orgs",
    REPO_DETAILS: (org: string, page: number, per_page: number) => `/repo-list?owner=${org}&page=${page}&per_page=${per_page}`,
    BRANCH_DETAILS: (org: string, repo: string) => `/branches?owner=${org}&repo=${repo}`,
    GET_ISSUE_TMPL_LIST: (branch: string, org: string, path: string, repo: string) => `/get-issue-wkfl-files?branch=${branch}&owner=${org}&path=${path}&repo=${repo}`,
    UPDATE_ISSUE_TMPL_LIST: "/sync-files",
  }

  /**
   * EXT_LINKS object:
   * This object contains API endpoints for managing external links functionality.
   * EXT_LINKS: Represents the base endpoint for external link operations.
   * ADD_EXT_LINKS: Endpoint for adding a new external link.
   * UPDATE_EXT_LINKS: Function that takes an ID and returns the endpoint for updating an external link.
   * CHECK_ACTIVE: Function that takes a label and returns the endpoint for checking if an external link is active.
   * GET: Endpoint for retrieving all external links.
   */
  public EXT_LINKS = {
    EXT_LINKS: "/external-link",
    ADD_EXT_LINKS: "/add",
    UPDATE_EXT_LINKS: (id: any) => `/update/${id}`,
    CHECK_ACTIVE: (label: any) => `/check/${label}`,
    GET: "/view-all",
  }

  /**
   * IMAGE object:
   * This object contains API endpoints for managing image operations.
   * IMAGE: Base endpoint for image operations.
   * DELETE: Function that takes an ID and returns the endpoint for deleting a specific image.
   * GET: Function that takes an ID and returns the endpoint for retrieving a specific image.
   * UPLOAD: Function that takes a fileName and optional ID, returns the endpoint for uploading an image.
   */
  public IMAGE = {
    IMAGE: "/image",
    DELETE: (id: number) => `/delete?id=${id}`,
    GET: (id: number) => `/get/${id}`,
    UPLOAD: (fileName: string, id?: number) => `/upload?fileName=${fileName}${id !== undefined ? `&id=${id}` : ''}`,
  }

  /**
   * HIL test results upload APIs.
   */
  public HIL_TEST_RESULTS_APIS = {
    HIL_TEST_RESULTS: "/hil-test-results",
    UPLOAD: "/upload",
  }

  /**
   * QG_CHECK_APIS object:
   * This object contains API endpoints for managing quality gate check operations.
   * QG_CHECK_UPDATE: Endpoint for updating quality gate check status for workflow jobs ("/wf-job/update-quality-gate-check")
   */
  public QG_CHECK_APIS = {
    QG_CHECK_UPDATE: "/wf-job/update-quality-gate-check",
  }

  /**
   * WF_SUMMARY_APIS object:
   * This object contains API endpoints for managing workflow summary operations.
   * WF_SUMMARY: Base endpoint for workflow summary operations ("/wkfl-sum")
   * GET_SUMMARY: Function that takes a jobId and returns the endpoint for retrieving workflow summary for that specific job
   */
  public WF_SUMMARY_APIS = {
    WF_SUMMARY: '/wkfl-sum',
    GET_SUMMARY: (jobId: any) => `/get-summary?dispatchId=${jobId}`,
  }

  public Dependent_CHECK_APIS = {
    DAG: '/dag',
    CONFIGURATIONS: '/workflow/configurations',
    SAVE_DEPENDENT_CHECKS: '/add',
    GET_DEPENDENT_CHECK: (id: number) => `/get/${id}`,
    GET_ALL_DEPENDENT_CHECKS: '/get_all',
    DELETE_DEPENDENT_CHECK: (id: number) => `/delete/${id}`,
    UPDATE_DEPENDENT_CHECK: (id: number) => `/update/${id}`
  }

  /**
   * WF_JOB_APIS object:
   * This object contains API endpoints for managing workflow job operations.
   * WF_JOB: Base endpoint for workflow job operations ("/wf-job")
   * JOB_HISTORY_TABLE: Endpoint for retrieving workflow execution history for a PR ("/job_history_table")
   */
  public WF_JOB_APIS = {
    WF_JOB: '/wf-job',
    JOB_HISTORY_TABLE: '/job_history_table',
  }
}
